'use client'

import { ReactNode } from 'react'
import { X } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from './Drawer'

interface SideDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback when drawer closes */
  onClose: () => void
  /** Drawer title */
  title: string
  /** Drawer content */
  children: ReactNode
  /** Direction to slide from (default: right) */
  direction?: 'left' | 'right'
  /** sticky-footer: content area does not scroll; child provides scrollable body + fixed bottom (e.g. Design Demo CTA). */
  contentLayout?: 'default' | 'sticky-footer'
}

/** Side drawer with header and close. contentLayout=sticky-footer lets children own scroll + sticky bottom. */
export default function SideDrawer({
  isOpen,
  onClose,
  title,
  children,
  direction = 'right',
  contentLayout = 'default',
}: SideDrawerProps) {
  const contentWrapperClass =
    contentLayout === 'sticky-footer'
      ? 'flex-1 flex flex-col min-h-0 overflow-hidden'
      : 'flex-1 overflow-y-auto'

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      direction={direction}
    >
      <DrawerContent direction={direction} showHandle={false}>
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-border p-4">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerClose asChild>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 touch-manipulation active:bg-muted/80 cursor-pointer"
              aria-label="Close drawer"
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </DrawerClose>
        </DrawerHeader>
        <div className={contentWrapperClass}>{children}</div>
      </DrawerContent>
    </Drawer>
  )
}
